import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

export const STOP_INTERVAL_MS = 14400000; // 4 hours
export const STOP_SCHEDULE_START = 8; // 08:00 UTC
export const STOP_SCHEDULE_END = 20; // 20:00 UTC
export const NONE_DAYS = "none";

export const DEFAULT_RESTORE = {
  operational_days: "Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday",
  scheduleStart: 8,
  scheduleEnd: 18,
  withinScheduleSleepTime: 3600000,
};

export interface StoppedConfigSnapshot {
  operational_days: string;
  scheduleStart: number;
  scheduleEnd: number;
  withinScheduleSleepTime: number;
}

/** True when the device has no operational days set (empty, null or "none"). */
export const areImagesStopped = (operationalDays: string | null | undefined): boolean => {
  if (operationalDays === null || operationalDays === undefined) return false;
  const value = String(operationalDays).trim().toLowerCase();
  return value === "" || value === NONE_DAYS;
};

export const parseSnapshot = (raw: any): StoppedConfigSnapshot | null => {
  if (!raw || typeof raw !== "object") return null;
  const days = typeof raw.operational_days === "string" ? raw.operational_days : null;
  if (!days || areImagesStopped(days)) return null;
  return {
    operational_days: days,
    scheduleStart: Number(raw.scheduleStart ?? DEFAULT_RESTORE.scheduleStart),
    scheduleEnd: Number(raw.scheduleEnd ?? DEFAULT_RESTORE.scheduleEnd),
    withinScheduleSleepTime: Number(
      raw.withinScheduleSleepTime ?? DEFAULT_RESTORE.withinScheduleSleepTime
    ),
  };
};

const parseCameraConfig = (raw: any): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
};

export const useDeviceImageCapture = () => {
  const { logChange } = useDeviceChangeLog();
  const [isBusy, setIsBusy] = useState(false);

  const pushUpdates = async (
    deviceId: string,
    updates: { field: string; value: string }[]
  ) => {
    for (const { field, value } of updates) {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", field);
      params.append("value", value);
      logChange(deviceId, field, value);
      const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!res.ok) throw new Error(`BuildShip update failed for ${field}`);
    }
  };

  /**
   * Stop image capture: snapshot the current settings, clear operational days
   * and throttle the schedule (4h interval, 08:00-20:00 UTC).
   */
  const stopImages = async (
    deviceId: string,
    current: {
      operationalDays?: string | null;
      cameraConfig?: any;
    }
  ): Promise<boolean> => {
    setIsBusy(true);
    try {
      const config = parseCameraConfig(current.cameraConfig);
      const snapshot: StoppedConfigSnapshot = {
        operational_days:
          current.operationalDays && !areImagesStopped(current.operationalDays)
            ? current.operationalDays
            : DEFAULT_RESTORE.operational_days,
        scheduleStart: Number(config.scheduleStart ?? DEFAULT_RESTORE.scheduleStart),
        scheduleEnd: Number(config.scheduleEnd ?? DEFAULT_RESTORE.scheduleEnd),
        withinScheduleSleepTime: Number(
          config.withinScheduleSleepTime ?? DEFAULT_RESTORE.withinScheduleSleepTime
        ),
      };

      await pushUpdates(deviceId, [
        { field: "deviceOperationalDays", value: NONE_DAYS },
        { field: "cameraConfig.withinScheduleSleepTime", value: String(STOP_INTERVAL_MS) },
        { field: "cameraConfig.scheduleStart", value: String(STOP_SCHEDULE_START) },
        { field: "cameraConfig.scheduleEnd", value: String(STOP_SCHEDULE_END) },
      ]);

      const { data: userData } = await supabase.auth.getUser();

      const { error } = await supabase
        .from("device_details")
        .update({
          device_operational_days: NONE_DAYS,
          camera_config: {
            ...config,
            withinScheduleSleepTime: STOP_INTERVAL_MS,
            scheduleStart: STOP_SCHEDULE_START,
            scheduleEnd: STOP_SCHEDULE_END,
          },
          stopped_config_snapshot: snapshot as any,
          images_stopped_at: new Date().toISOString(),
          images_stopped_by: userData?.user?.id ?? null,
        })
        .eq("device_id", deviceId);
      if (error) throw error;

      toast.success("Image capture stopped — days cleared, 4h interval, 08:00–20:00 UTC");
      return true;
    } catch (error) {
      console.error("Failed to stop images:", error);
      toast.error("Failed to stop image capture");
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  /**
   * Restart image capture by restoring the saved snapshot (or sensible defaults).
   */
  const startImages = async (
    deviceId: string,
    current: {
      snapshot?: any;
      cameraConfig?: any;
    }
  ): Promise<StoppedConfigSnapshot | null> => {
    setIsBusy(true);
    try {
      const restore = parseSnapshot(current.snapshot) ?? { ...DEFAULT_RESTORE };
      const config = parseCameraConfig(current.cameraConfig);

      await pushUpdates(deviceId, [
        { field: "deviceOperationalDays", value: restore.operational_days },
        {
          field: "cameraConfig.withinScheduleSleepTime",
          value: String(restore.withinScheduleSleepTime),
        },
        { field: "cameraConfig.scheduleStart", value: String(restore.scheduleStart) },
        { field: "cameraConfig.scheduleEnd", value: String(restore.scheduleEnd) },
      ]);

      const { error } = await supabase
        .from("device_details")
        .update({
          device_operational_days: restore.operational_days,
          camera_config: {
            ...config,
            withinScheduleSleepTime: restore.withinScheduleSleepTime,
            scheduleStart: restore.scheduleStart,
            scheduleEnd: restore.scheduleEnd,
          },
          stopped_config_snapshot: null,
          images_stopped_at: null,
          images_stopped_by: null,
        })
        .eq("device_id", deviceId);
      if (error) throw error;

      toast.success("Image capture restarted");
      return restore;
    } catch (error) {
      console.error("Failed to start images:", error);
      toast.error("Failed to restart image capture");
      return null;
    } finally {
      setIsBusy(false);
    }
  };

  return { stopImages, startImages, isBusy };
};
